import type { ReactNode } from 'react'
import type { TileKey } from '../lib/homeTiles'
import type { StaffKey } from '../lib/staffText'
import { useStaffText } from '../ui/StaffText'

/** Sample pictures of each work app for the home cards: fixed sample data, not the branch's own. */

const BARS = [36, 62, 49, 80, 65, 92, 73, 100]

function Window({ name, aside, children }: { name: StaffKey; aside: ReactNode; children: ReactNode }) {
  const { say } = useStaffText()
  return (
    <div className="ws-mini">
      <div className="ws-mini__top">
        <span className="ws-mini__dot" />
        <span className="ws-mini__name">{say(name)}</span>
        <span className="ws-mini__aside">{aside}</span>
      </div>
      <div className="ws-mini__body">{children}</div>
    </div>
  )
}

export function HomePreview({ tile }: { tile: TileKey }) {
  const { say } = useStaffText()
  switch (tile) {
    case 'counter':
      return (
        <Window name="entry.app.counter" aside={say('entry.preview.desk')}>
          <div className="ws-mini__caption">{say('entry.preview.nowServing')}</div>
          <div className="ws-mini__ticket">
            A-020 <span className="ws-mini__chip">{say('entry.preview.called')}</span>
          </div>
          <div className="ws-mini__line">{say('entry.preview.service')}</div>
          <div className="ws-mini__button">
            {say('entry.preview.callNext')} <b>→</b>
          </div>
        </Window>
      )
    case 'reception':
      return (
        <Window name="entry.app.reception" aside={say('entry.preview.newVisitor')}>
          <div className="ws-mini__caption">{say('entry.preview.issueTicket')}</div>
          <div className="ws-mini__service">
            <span className="ws-mini__icon" />
            <span>
              <strong>{say('entry.preview.service')}</strong>
              <small>{say('entry.preview.chooseService')}</small>
            </span>
          </div>
          <div className="ws-mini__button">
            {say('entry.preview.issueTicket')} <b>→</b>
          </div>
          <div className="ws-mini__slip">
            <span>{say('entry.preview.slip')}</span>
            <b>A-021</b>
            <small>{say('entry.preview.service')}</small>
          </div>
        </Window>
      )
    case 'bookings':
      return (
        <Window name="entry.app.appointments" aside={say('entry.preview.weekday')}>
          <div className="ws-mini__caption">{say('entry.preview.schedule')}</div>
          {(
            [
              ['09:30', 'entry.preview.opening'],
              ['10:00', 'entry.preview.consultation'],
            ] as const
          ).map(([at, what]) => (
            <div className="ws-mini__booking" key={at}>
              <b>{at}</b>
              <span>
                <strong>{say(what)}</strong>
                <small>{say('entry.preview.confirmed')}</small>
              </span>
              <i />
            </div>
          ))}
        </Window>
      )
    case 'floor':
      return (
        <Window name="entry.card.floor" aside={<>● {say('entry.preview.live')}</>}>
          <div className="ws-mini__caption">{say('entry.preview.overview')}</div>
          <div className="ws-mini__metrics">
            <div>
              <strong>24</strong>
              <small>{say('entry.preview.waiting')}</small>
            </div>
            <div>
              <strong>08</strong>
              <small>{say('entry.preview.serving')}</small>
            </div>
            <div>
              <strong>
                06<span>m</span>
              </strong>
              <small>{say('entry.preview.avgWait')}</small>
            </div>
          </div>
          <div className="ws-mini__bars">
            {BARS.map((h, i) => (
              <i key={i} style={{ height: `${h}%` }} />
            ))}
          </div>
        </Window>
      )
    default:
      return null
  }
}
